import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlay, FaPause, FaTrophy, FaCoins, FaArrowLeft } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { formatAmount } from '../utils/currency';
import { toast } from 'react-toastify';

// Tetris constants
const ROWS = 20;
const COLS = 10;
const BLOCK_SIZE = 30;
const COLORS = [
  '#000000', // Empty
  '#FF0D72', // I
  '#0DC2FF', // J
  '#0DFF72', // L
  '#F538FF', // O
  '#FF8E0D', // S
  '#FFE138', // T
  '#3877FF', // Z
];

// Tetromino shapes
const SHAPES = [
  [[1, 1, 1, 1]], // I
  [[2, 0, 0], [2, 2, 2]], // J
  [[0, 0, 3], [3, 3, 3]], // L
  [[4, 4], [4, 4]], // O
  [[0, 5, 5], [5, 5, 0]], // S
  [[0, 6, 0], [6, 6, 6]], // T
  [[7, 7, 0], [0, 7, 7]], // Z
];

interface TetrisGameConfig {
  isEnabled: boolean;
  minStake: number;
  maxStake: number;
  easyTargetScore: number;
  mediumTargetScore: number;
  hardTargetScore: number;
  easyMultiplier: number;
  mediumMultiplier: number;
  hardMultiplier: number;
}

interface GameSession {
  id: number;
  difficulty: string;
  stake: number;
  targetScore: number;
  multiplier: number;
  potentialWin: number;
}

interface TetrisGameProps {
  onBack: () => void;
  onBalanceUpdate?: () => void;
}

const TetrisGame: React.FC<TetrisGameProps> = ({ onBack, onBalanceUpdate }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);

  // Setup state
  const [difficulty, setDifficulty] = useState<'EASY' | 'MEDIUM' | 'HARD'>('EASY');
  const [stake, setStake] = useState<number>(10);
  const [config, setConfig] = useState<TetrisGameConfig | null>(null);
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(false);

  // Game board
  const [board, setBoard] = useState<number[][]>(() =>
    Array(ROWS).fill(null).map(() => Array(COLS).fill(0))
  );
  const [currentPiece, setCurrentPiece] = useState<any>(null);

  // Fetch game config
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await apiService.get('/api/igaming/tetris/config');
      if (response.success) {
        setConfig(response.data);
      } else {
        toast.error('Failed to load game configuration');
      }
    } catch (err) {
      console.error('Failed to fetch Tetris config:', err);
      toast.error('Failed to load game configuration');
    }
  };

  const getTargetScore = () => {
    if (!config) return 0;
    switch (difficulty) {
      case 'EASY': return config.easyTargetScore;
      case 'MEDIUM': return config.mediumTargetScore;
      case 'HARD': return config.hardTargetScore;
      default: return 0;
    }
  };

  const getMultiplier = () => {
    if (!config) return 0;
    switch (difficulty) {
      case 'EASY': return config.easyMultiplier;
      case 'MEDIUM': return config.mediumMultiplier;
      case 'HARD': return config.hardMultiplier;
      default: return 0;
    }
  };

  const startGame = async () => {
    setLoading(true);
    try {
      const response = await apiService.post('/api/igaming/tetris/start', {
        difficulty,
        stake
      });

      if (response.success) {
        setSession(response.data);
        toast.success(`Game started! Reach ${getTargetScore()} points to win ${formatAmount(stake * getMultiplier(), 'EUR')}!`);
        initializeGame();
      } else {
        toast.error('Failed to start game');
      }
    } catch (err: any) {
      console.error('Failed to start game:', err);
      toast.error(err.response?.data?.message || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const initializeGame = () => {
    const newBoard = Array(ROWS).fill(null).map(() => Array(COLS).fill(0));
    setBoard(newBoard);
    setScore(0);
    setLevel(1);
    setLines(0);
    setGameOver(false);
    setGamePaused(false);
    setGameStarted(true);
    spawnPiece();
  };

  const spawnPiece = () => {
    const shapeIndex = Math.floor(Math.random() * SHAPES.length);
    const shape = SHAPES[shapeIndex];
    const piece = {
      shape,
      color: shapeIndex + 1,
      x: Math.floor(COLS / 2) - Math.floor(shape[0].length / 2),
      y: 0
    };

    // Check if piece can be placed
    if (!canPlacePiece(piece, board)) {
      endGame();
      return;
    }

    setCurrentPiece(piece);
  };

  const canPlacePiece = (piece: any, board: number[][]) => {
    for (let row = 0; row < piece.shape.length; row++) {
      for (let col = 0; col < piece.shape[row].length; col++) {
        if (piece.shape[row][col]) {
          const boardRow = piece.y + row;
          const boardCol = piece.x + col;

          if (
            boardRow < 0 ||
            boardRow >= ROWS ||
            boardCol < 0 ||
            boardCol >= COLS ||
            board[boardRow][boardCol]
          ) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const movePiece = (dx: number, dy: number) => {
    if (!currentPiece || gamePaused || gameOver) return;

    const newPiece = { ...currentPiece, x: currentPiece.x + dx, y: currentPiece.y + dy };

    if (canPlacePiece(newPiece, board)) {
      setCurrentPiece(newPiece);
      return true;
    } else if (dy > 0) {
      // Piece hit bottom, merge it
      mergePiece();
      return false;
    }
    return false;
  };

  const rotatePiece = () => {
    if (!currentPiece || gamePaused || gameOver) return;

    const rotated = currentPiece.shape[0].map((_: any, index: number) =>
      currentPiece.shape.map((row: any) => row[index]).reverse()
    );

    const newPiece = { ...currentPiece, shape: rotated };

    if (canPlacePiece(newPiece, board)) {
      setCurrentPiece(newPiece);
    }
  };

  const mergePiece = () => {
    if (!currentPiece) return;

    const newBoard = board.map(row => [...row]);

    for (let row = 0; row < currentPiece.shape.length; row++) {
      for (let col = 0; col < currentPiece.shape[row].length; col++) {
        if (currentPiece.shape[row][col]) {
          const boardRow = currentPiece.y + row;
          const boardCol = currentPiece.x + col;
          if (boardRow >= 0 && boardRow < ROWS && boardCol >= 0 && boardCol < COLS) {
            newBoard[boardRow][boardCol] = currentPiece.color;
          }
        }
      }
    }

    setBoard(newBoard);
    checkLines(newBoard);
    spawnPiece();
  };

  const checkLines = (board: number[][]) => {
    let linesCleared = 0;
    const newBoard = board.filter(row => {
      if (row.every(cell => cell !== 0)) {
        linesCleared++;
        return false;
      }
      return true;
    });

    // Add empty rows at the top
    while (newBoard.length < ROWS) {
      newBoard.unshift(Array(COLS).fill(0));
    }

    if (linesCleared > 0) {
      setBoard(newBoard);
      setLines(prev => prev + linesCleared);
      
      // Calculate score
      const points = [0, 100, 300, 500, 800][linesCleared] * level;
      setScore(prev => prev + points);

      // Level up every 10 lines
      if ((lines + linesCleared) / 10 > level) {
        setLevel(prev => prev + 1);
      }
    }
  };

  const hardDrop = () => {
    if (!currentPiece || gamePaused || gameOver) return;
    
    let newY = currentPiece.y;
    while (canPlacePiece({ ...currentPiece, y: newY + 1 }, board)) {
      newY++;
    }
    
    setCurrentPiece({ ...currentPiece, y: newY });
    setTimeout(() => mergePiece(), 50);
  };

  const endGame = async () => {
    setGameOver(true);
    setGameStarted(false);

    if (!session) return;

    try {
      const response = await apiService.post('/api/igaming/tetris/end', {
        sessionId: session.id,
        finalScore: score
      });

      if (response.success) {
        const result = response.data;
        if (result.status === 'WON') {
          toast.success(`🎉 You won ${formatAmount(result.actualWin, 'EUR')}! Score: ${result.actualScore}/${result.targetScore}`);
        } else {
          toast.info(`Game Over! Score: ${result.actualScore}/${result.targetScore}. Better luck next time!`);
        }
        
        // Update balance
        if (onBalanceUpdate) {
          onBalanceUpdate();
        }
      }
    } catch (err) {
      console.error('Failed to end game:', err);
      toast.error('Failed to process game result');
    }
  };

  // Game loop
  const gameLoop = useCallback((timestamp: number) => {
    if (!gameStarted || gamePaused || gameOver) {
      requestRef.current = requestAnimationFrame(gameLoop);
      return;
    }

    const deltaTime = timestamp - lastTimeRef.current;
    const dropInterval = 1000 - (level - 1) * 100; // Speed increases with level

    if (deltaTime > dropInterval) {
      movePiece(0, 1);
      lastTimeRef.current = timestamp;
    }

    requestRef.current = requestAnimationFrame(gameLoop);
  }, [gameStarted, gamePaused, gameOver, level, currentPiece, board]);

  useEffect(() => {
    requestRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [gameLoop]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted || gamePaused || gameOver) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          movePiece(-1, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          movePiece(1, 0);
          break;
        case 'ArrowDown':
          e.preventDefault();
          movePiece(0, 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          rotatePiece();
          break;
        case ' ':
          e.preventDefault();
          hardDrop();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStarted, gamePaused, gameOver, currentPiece, board]);

  // Render game
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas with dark background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid with more visible lines
    ctx.strokeStyle = '#0f3460';
    ctx.lineWidth = 1;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        ctx.strokeRect(col * BLOCK_SIZE, row * BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE);
      }
    }

    // Draw board
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (board[row][col]) {
          ctx.fillStyle = COLORS[board[row][col]];
          ctx.fillRect(col * BLOCK_SIZE + 1, row * BLOCK_SIZE + 1, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
        }
      }
    }

    // Draw current piece
    if (currentPiece && !gameOver) {
      ctx.fillStyle = COLORS[currentPiece.color];
      for (let row = 0; row < currentPiece.shape.length; row++) {
        for (let col = 0; col < currentPiece.shape[row].length; col++) {
          if (currentPiece.shape[row][col]) {
            const x = (currentPiece.x + col) * BLOCK_SIZE + 1;
            const y = (currentPiece.y + row) * BLOCK_SIZE + 1;
            ctx.fillRect(x, y, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
          }
        }
      }
    }

    // Draw "Ready" text if game hasn't started
    if (!gameStarted && !gameOver) {
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('READY TO PLAY', canvas.width / 2, canvas.height / 2);
      ctx.font = '16px Arial';
      ctx.fillText('Place your bet and start!', canvas.width / 2, canvas.height / 2 + 30);
    }
  }, [board, currentPiece, gameOver, gameStarted]);

  if (!config) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!config.isEnabled) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Tetris game is currently disabled</p>
        <button onClick={onBack} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Back to iGaming
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <button
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <FaArrowLeft className="mr-2" />
        Back to iGaming
      </button>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Game Canvas */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <FaTrophy className="mr-2 text-yellow-500" />
              Tetris Challenge
            </h2>

            <div className="flex justify-center bg-gray-900 p-4 rounded-lg">
              <canvas
                ref={canvasRef}
                width={COLS * BLOCK_SIZE}
                height={ROWS * BLOCK_SIZE}
                className="border-4 border-gray-700 rounded-lg"
                style={{ backgroundColor: '#1a1a2e' }}
              />
            </div>

            {/* Controls */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {!gameStarted && !gameOver && (
                <button
                  onClick={startGame}
                  disabled={loading || !session}
                  className="col-span-2 flex items-center justify-center px-6 py-3 bg-green-600 text-black rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                      Starting...
                    </>
                  ) : (
                    <>
                      <FaPlay className="mr-2" />
                      Start Game
                    </>
                  )}
                </button>
              )}

              {gameStarted && (
                <button
                  onClick={() => setGamePaused(!gamePaused)}
                  className="col-span-2 flex items-center justify-center px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  <FaPause className="mr-2" />
                  {gamePaused ? 'Resume' : 'Pause'}
                </button>
              )}

              {gameOver && (
                <button
                  onClick={() => {
                    setSession(null);
                    setGameOver(false);
                  }}
                  className="col-span-2 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Play Again
                </button>
              )}
            </div>

            {/* Mobile Controls */}
            <div className="mt-4 md:hidden">
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => movePiece(-1, 0)} className="px-4 py-2 bg-gray-200 text-gray-900 font-bold rounded hover:bg-gray-300">←</button>
                <button onClick={rotatePiece} className="px-4 py-2 bg-gray-200 text-gray-900 font-bold rounded hover:bg-gray-300">↻</button>
                <button onClick={() => movePiece(1, 0)} className="px-4 py-2 bg-gray-200 text-gray-900 font-bold rounded hover:bg-gray-300">→</button>
                <button onClick={() => movePiece(0, 1)} className="px-4 py-2 bg-gray-200 text-gray-900 font-bold rounded hover:bg-gray-300">↓</button>
                <button onClick={hardDrop} className="col-span-2 px-4 py-2 bg-gray-200 text-gray-900 font-bold rounded hover:bg-gray-300">Drop</button>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
              <h3 className="font-semibold mb-2">Controls:</h3>
              <ul className="space-y-1 text-gray-700">
                <li>← → : Move left/right</li>
                <li>↑ : Rotate</li>
                <li>↓ : Soft drop</li>
                <li>Space : Hard drop</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Stats */}
          {gameStarted && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="font-bold text-lg mb-4">Game Stats</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Score</p>
                  <p className="text-2xl font-bold text-blue-600">{score}</p>
                  <p className="text-xs text-gray-500">Target: {getTargetScore()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Level</p>
                  <p className="text-xl font-semibold">{level}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Lines</p>
                  <p className="text-xl font-semibold">{lines}</p>
                </div>
                {session && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-600">Potential Win</p>
                    <p className="text-xl font-bold text-green-600">{formatAmount(session.potentialWin, 'EUR')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Setup */}
          {!gameStarted && !session && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center">
                <FaCoins className="mr-2 text-yellow-500" />
                Game Setup
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="EASY">Easy (Score: {config.easyTargetScore}, {config.easyMultiplier}x)</option>
                    <option value="MEDIUM">Medium (Score: {config.mediumTargetScore}, {config.mediumMultiplier}x)</option>
                    <option value="HARD">Hard (Score: {config.hardTargetScore}, {config.hardMultiplier}x)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stake ({formatAmount(config.minStake, 'EUR')} - {formatAmount(config.maxStake, 'EUR')})
                  </label>
                  <input
                    type="number"
                    value={stake}
                    onChange={(e) => setStake(Number(e.target.value))}
                    min={config.minStake}
                    max={config.maxStake}
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Potential Win</p>
                  <p className="text-2xl font-bold text-green-600">{formatAmount(stake * getMultiplier(), 'EUR')}</p>
                  <p className="text-xs text-gray-600 mt-1">Reach {getTargetScore()} points to win!</p>
                </div>

                <button
                  onClick={startGame}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-black rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaCoins className="mr-2" />
                      Place Bet & Start
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TetrisGame;

